import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useGetWebsiteConfig } from "@/hooks/use-configuration";
import { formatDuration } from "@/lib/internalApis";
import useFlightBookingStore from "@/lib/useFlightBookingStore";
import { X } from "lucide-react";
import { useSearchParams } from "next/navigation";
import React from "react";
import { formatDate } from "./viewDialog";

const Whatsapp = () => {
  const { viewData } = useFlightBookingStore();
  const searchParams = useSearchParams();
  const { data: websiteConfig } = useGetWebsiteConfig();
  const getParam = (key: string) => searchParams?.get(key) || "";
  const queryData = JSON.parse(getParam("data") || "{}");
  const queryMetaData = JSON.parse(getParam("metadata") || "{}");
  const queryTripType = getParam("tripType");
  const [showAlert, setShowAlert] = React.useState(false);

  const generateCopyText = () => {
    let text = `Hello, please find details with regards to your flights(s) query for:\n`;

    queryMetaData?.forEach((route: any) => {
      text += `${route.fromCityOrAirport} - ${route.toCityOrAirport}\n`;
    });

    Object.keys(queryData?.paxInfo || {}).forEach((key: any) => {
      if (queryData?.paxInfo[key] !== "0") {
        text += `${queryData?.paxInfo[key]} ${key}\n`;
      }
    });

    text += `${queryData?.cabinClass}\n\n`;
    text += `Below mentioned prices are the total price(s) inclusive of taxes:\n\n`;

    viewData.forEach((data: any) => {
      let { sI } = data;

      sI = sI.reduce(
        (acc: any, item: any) => {
          if (item.isRs) {
            acc.ret.push(item);
          } else {
            acc.dep.push(item);
          }
          return acc;
        },
        { dep: [], ret: [] }
      );

      let { dep, ret } = sI;

      const firstDep = dep[0];
      const lastDep = dep[dep?.length - 1];

      const depTotalDuration =
        dep.length < 2
          ? dep[0]?.duration
          : firstDep?.duration + (lastDep?.duration ?? 0) + (firstDep.cT ?? 0);

      const firstRet = ret[0];
      const lastRet = ret[ret?.length - 1];
      const retTotalDuration =
        ret.length < 2
          ? ret[0]?.duration
          : firstRet?.duration + (lastRet?.duration ?? 0) + (firstRet.cT ?? 0);

      // Departure details
      if (dep.length > 0) {
        text += `${data.tripType} (Departure)\n`;
        text += `🛫 ${dep[0].fD.aI.name} (${dep
          .map((s: any) => s.fD.fN)
          .join(", ")})\n`;
        text += `${dep[0].da.cityCode}(${dep[0].da.city}) - ${
          dep[dep.length - 1].aa.cityCode
        }(${dep[dep.length - 1].aa.city}) via ${dep
          .map((s: any, index: number) =>
            index > 0 && index < data.sI.length
              ? `${s.da.cityCode}(${s.da.city})`
              : ""
          )
          .filter(Boolean)
          .join(", ")}\n`;
        text += `Departure: ${formatDate(dep[0].dt)}\n`;
        text += `Arrival: ${formatDate(dep[dep.length - 1].at)}\n`;
        text += `Duration: ${formatDuration(depTotalDuration)} / ${
          dep.length > 1
            ? `${dep.length - 1} Stop${dep.length - 1 > 1 ? "s" : ""}`
            : "Non Stop"
        }\n`;
      }

      // Return details
      if (ret.length > 0) {
        text += `${data.tripType} (Return)\n`;
        text += `🛬 ${ret[0].fD.aI.name} (${ret
          .map((s: any) => s.fD.fN)
          .join(", ")})\n`;
        text += `${ret[0].da.cityCode}(${ret[0].da.city}) - ${
          ret[ret.length - 1].aa.cityCode
        }(${ret[ret.length - 1].aa.city}) via ${ret
          .map((s: any, index: number) =>
            index > 0 && index < data.sI.length
              ? `${s.da.cityCode}(${s.da.city})`
              : ""
          )
          .filter(Boolean)
          .join(", ")}\n`;
        text += `Departure: ${formatDate(ret[0].dt)}\n`;
        text += `Arrival: ${formatDate(ret[ret.length - 1].at)}\n`;
        text += `Duration: ${formatDuration(retTotalDuration)} / ${
          ret.length > 1
            ? `${ret.length - 1} Stop${ret.length - 1 > 1 ? "s" : ""}`
            : "Non Stop"
        }\n`;
      }

     if(data?.fare){
       Object.keys(data?.fare?.fd).forEach((key: any) => {
        text += `${key}: ${data.fare.fd[key].bI.iB} | ${
          data.fare.fd[key].bI.cB
        }${data.fare.fd[key].mI ? " / Free Meal" : ""}\n`;
      });
     }
      text += `Price: ${data.totalPrice}\n\n`;
    });

    text += `Thank you for choosing ${websiteConfig?.businessName}\n`;
    text += `In case of any support:\n`;
    text += `☎️ Contact: ${websiteConfig?.customerSupport.mobile}\n`;
    text += `✉ Email: ${websiteConfig?.customerSupport.email}\n\n`;
    text += `Airline ticket pricing is dynamic. Fares are valid as of now and might change at the time of issuance.`;

    return text;
  };

  const handleShareOnWhatsapp = () => {
    if (viewData.length === 0) {
      setShowAlert(true);
    } else {
      const text = generateCopyText();
      // Encode the text for use in URL
      const encodedText = encodeURIComponent(text);
      // WhatsApp Web URL with encoded text
      const whatsappURL = `https://web.whatsapp.com/send?text=${encodedText}`;

      // Open WhatsApp Web in new tab
      window.open(whatsappURL, "_blank");
    }
  };

  return (
    <>
      {showAlert && (
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50">
          <Alert className="bg-white shadow-lg">
            <AlertTitle className="flex items-center justify-between">
              No Flights Available
              <button onClick={() => setShowAlert(false)}>
                <X />
              </button>
            </AlertTitle>
            <AlertDescription>
              There are no flight details available for your query.
            </AlertDescription>
          </Alert>
        </div>
      )}

      <div>
        <button className="text-green-500" onClick={handleShareOnWhatsapp}>
          WhatsApp
        </button>
      </div>
    </>
  );
};

export default Whatsapp;
